import { useState, useCallback, useRef, useEffect } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { useObserver } from 'mobx-react-lite';
import { PublicContextSessionStatus, useGetPublicContextSessionQuery } from 'types/graphql';
import { MeConsumer } from 'shared/graphql/user/queries/me-consumer.gql';
import useStores from 'shared/hooks/use-stores';
import { usePublicContextSessionPusher } from 'src/hooks/use-public-context-session-pusher';
import { parseSessionStatus } from 'src/components/kiosk-pro/kiosk-pro-shared/session-status';

type UsePublicSessionLoginHandlerReturnType = {
  sessionState: {
    status: PublicContextSessionStatus;
    hasAttemptedLogin: boolean;
  };
  isUserLoggedInSuccessfully: boolean;
  userFirstName: string;
};

export function usePublicSessionLoginHandler(sessionId: string): UsePublicSessionLoginHandlerReturnType {
  const { User } = useStores();
  const apolloClient = useApolloClient();
  const [sessionState, setSessionState] = useState({
    status: PublicContextSessionStatus.unclaimed,
    hasAttemptedLogin: false,
  });
  const isLoggingInRef = useRef(false);
  const hasAttemptedLoginRef = useRef(false);

  const isUserLoggedIn = useObserver(() => User?.isLoggedIn ?? false);
  const userFirstName = useObserver(() => User?.firstName ?? '');
  const birthday = useObserver(() => User.birthday);
  const phone = useObserver(() => User.phone);

  const hasUserLoggedInAndClaimedStatus = sessionState.status === PublicContextSessionStatus.claimed && isUserLoggedIn;

  const { data, refetch } = useGetPublicContextSessionQuery({
    variables: {
      id: sessionId,
    },
    pollInterval: 15000,
  });

  usePublicContextSessionPusher({
    sessionId,
    enabled: !hasUserLoggedInAndClaimedStatus,
    onSessionUpdated: (sessionData) => {
      setSessionState((prev) => ({
        ...prev,
        status: parseSessionStatus(sessionData.status),
      }));
      void refetch();
    },
    onSessionClaimed: (claimData) => {
      setSessionState((prev) => ({
        ...prev,
        status: parseSessionStatus(claimData.status),
      }));
    },
  });

  const isUserLoggedInSuccessfully = hasUserLoggedInAndClaimedStatus && birthday && phone;

  useEffect(() => {
    if (hasUserLoggedInAndClaimedStatus) {
      void User.refetch();
    }
  }, [hasUserLoggedInAndClaimedStatus, User]);

  // Continue polling User data until birthday and phone are available. They will be updated from the public session on consumers phone.
  useEffect(() => {
    if (hasUserLoggedInAndClaimedStatus && (!birthday || !phone)) {
      const interval = setInterval(() => {
        void User.refetch();
      }, 2000);

      return () => clearInterval(interval);
    }
    return undefined;
  }, [hasUserLoggedInAndClaimedStatus, birthday, phone, User]);

  const handleUserLogin = useCallback(async (): Promise<void> => {
    if (!data?.getPublicContextSession?.accessToken || hasAttemptedLoginRef.current || isLoggingInRef.current) {
      return;
    }

    try {
      isLoggingInRef.current = true;

      // First get the user data using the access token
      const { data: userData } = await apolloClient.query({
        query: MeConsumer,
        fetchPolicy: 'no-cache',
        context: {
          headers: {
            authorization: data.getPublicContextSession.accessToken,
          },
        },
      });

      // Then login with both the access token and user data
      await User.login({
        accessToken: data.getPublicContextSession.accessToken,
        user: userData.meConsumer,
      });

      hasAttemptedLoginRef.current = true;
      setSessionState((prev) => ({ ...prev, hasAttemptedLogin: true }));
    } catch (loginError) {
      console.error('Error logging in user:', loginError);
      hasAttemptedLoginRef.current = true;
      setSessionState((prev) => ({ ...prev, hasAttemptedLogin: true }));
    } finally {
      isLoggingInRef.current = false;
    }
  }, [data?.getPublicContextSession?.accessToken, apolloClient, User]);

  useEffect(() => {
    void handleUserLogin();

    const publicContextSession = data?.getPublicContextSession;
    if (publicContextSession?.status) {
      setSessionState((prev) => ({
        ...prev,
        status: publicContextSession.status,
      }));
    }
  }, [handleUserLogin, data]);

  return {
    sessionState,
    isUserLoggedInSuccessfully,
    userFirstName,
  };
}
